import { Fragment, h, type ComponentChildren, type VNode } from "preact";

// Restricted Markdown subset for action descriptions: bold, italic, underline,
// bullet lists, https links. Stored as Markdown text; rendered WITHOUT ever producing raw HTML —
// the parser emits a small AST and renderRichText maps it to Preact elements,
// so there is no XSS surface. Mirror of frontend/src/lib/richtext.ts — keep
// the parser and its test vectors in sync (frontend/embed parity).

export interface InlineSpan {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  href?: string;
}

export type RichBlock =
  | { type: "paragraph"; lines: InlineSpan[][] }
  | { type: "list"; items: InlineSpan[][] };

// Emphasis markers require a non-space directly after the opening marker
// (Markdown flanking rule), so "5 * 3" stays literal. Underline uses __…__
// (our own subset convention; standard Markdown has no underline).
// Links: [text](https://…) only. Any other scheme stays literal text, so no
// javascript:/data: URL can ever reach an href. One level of balanced
// parentheses is allowed inside the URL (Wikipedia-style paths).
const URL_SRC = String.raw`https:\/\/(?:[^\s()<>"]|\([^\s()<>"]*\))+`;
const LINK_SRC = String.raw`\[(?<lt>[^\[\]\n]+)\]\((?<url>${URL_SRC})\)`;
const EMPH_SRC = String.raw`\*\*(?<b>[^*\s][^*]*?)\*\*|\*(?<i>[^*\s][^*]*?)\*|__(?<u>[^_\s][^_]*?)__`;
const BULLET_RE = /^[-*]\s+/;

type SpanStyle = Omit<InlineSpan, "text">;

// Emphasis may wrap a link and a link may carry emphasis in its text, but
// nothing nests further: spans stay flat, each carrying its full style.
function parseInline(text: string, base: SpanStyle = {}): InlineSpan[] {
  const inEmphasis = !!(base.bold || base.italic || base.underline);
  if (base.href && inEmphasis) return [{ text, ...base }];
  const src = base.href ? EMPH_SRC : inEmphasis ? LINK_SRC : `${LINK_SRC}|${EMPH_SRC}`;
  const re = new RegExp(src, "g");
  const spans: InlineSpan[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) spans.push({ text: text.slice(last, m.index), ...base });
    const g = m.groups ?? {};
    if (g.url != null) spans.push(...parseInline(g.lt, { ...base, href: g.url }));
    else if (g.b != null) spans.push(...parseInline(g.b, { ...base, bold: true }));
    else if (g.i != null) spans.push(...parseInline(g.i, { ...base, italic: true }));
    else spans.push(...parseInline(g.u, { ...base, underline: true }));
    last = re.lastIndex;
  }
  if (last < text.length) spans.push({ text: text.slice(last), ...base });
  return spans.length ? spans : [{ text, ...base }];
}

export function parseRichText(md: string | null | undefined): RichBlock[] {
  const text = (md ?? "").replace(/\r\n?/g, "\n");
  const blocks: RichBlock[] = [];
  for (const raw of text.split(/\n{2,}/)) {
    const lines = raw.split("\n").map((l) => l.replace(/\s+$/, "")).filter((l) => l.trim() !== "");
    if (lines.length === 0) continue;
    if (BULLET_RE.test(lines[0].trimStart())) {
      const items = lines
        .map((l) => l.trimStart())
        .filter((l) => BULLET_RE.test(l))
        .map((l) => parseInline(l.replace(BULLET_RE, "")));
      blocks.push({ type: "list", items });
    } else {
      blocks.push({ type: "paragraph", lines: lines.map((l) => parseInline(l)) });
    }
  }
  return blocks;
}

// Plain-text projection for compact contexts (tile intro, title fallback).
export function stripRichText(md: string | null | undefined): string {
  return (md ?? "")
    .replace(new RegExp(LINK_SRC, "g"), "$<lt>")
    .replace(/\*\*([^*\s][^*]*?)\*\*/g, "$1")
    .replace(/\*([^*\s][^*]*?)\*/g, "$1")
    .replace(/__([^_\s][^_]*?)__/g, "$1")
    .replace(/^[\t ]*[-*]\s+/gm, "")
    .replace(/\s*\n\s*/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

const LINK_REL = "nofollow noopener noreferrer ugc";

// Consecutive spans sharing an href belong to one link.
function groupByHref(spans: InlineSpan[]): { href?: string; spans: InlineSpan[] }[] {
  const groups: { href?: string; spans: InlineSpan[] }[] = [];
  for (const s of spans) {
    const prev = groups[groups.length - 1];
    if (prev && s.href && prev.href === s.href) prev.spans.push(s);
    else groups.push({ href: s.href, spans: [s] });
  }
  return groups;
}

function renderSpan(s: InlineSpan, key: number): VNode {
  let node: ComponentChildren = s.text;
  if (s.underline) node = h("u", null, node);
  if (s.italic) node = h("em", null, node);
  if (s.bold) node = h("strong", null, node);
  return h(Fragment, { key }, node);
}

function renderSpans(spans: InlineSpan[]): ComponentChildren[] {
  return groupByHref(spans).map((g, i) =>
    g.href
      ? h("a", { key: i, href: g.href, rel: LINK_REL, target: "_blank" }, g.spans.map(renderSpan))
      : h(Fragment, { key: i }, g.spans.map(renderSpan)),
  );
}

export function renderRichText(md: string | null | undefined): VNode {
  const blocks = parseRichText(md);
  return h(
    Fragment,
    null,
    blocks.map((b, i) => {
      if (b.type === "list") {
        return h(
          "ul",
          { key: i },
          b.items.map((it, j) => h("li", { key: j }, renderSpans(it))),
        );
      }
      return h(
        "p",
        { key: i },
        b.lines.map((line, li) =>
          h(
            Fragment,
            { key: li },
            renderSpans(line),
            li < b.lines.length - 1 ? h("br", {}) : null,
          ),
        ),
      );
    }),
  );
}
